import { faMountain } from '@fortawesome/free-solid-svg-icons';

/**
 * Trade Ops Cargo (solids) walkthrough.
 *
 * Cargo and Bunkers share the same component (TradeOperationsLayout) with a
 * different ``commodityType`` prop. Tabs are in-page React state, not URL
 * routes, so we use the engine's ``clickBefore`` field to switch tabs the
 * same way a real user would - by clicking the tab button.
 *
 * The flow walks: dashboard -> bid/offer tab -> recaps tab -> deliveries tab,
 * highlighting each tab's primary "create new" action. Permission-gated
 * tabs (Budget, Operations Access) are intentionally excluded - they are
 * highly role-specific and would muddy a generic onboarding tour.
 */

const CARGO_ROUTE = '/dashboard/trades/solids';

const cargoTour = {
    id: 'cargo',
    title: 'Trade Ops Cargo',
    description:
        'Walk through Cargo: shipment overview, bid/offer flow, trade recaps and delivery creation.',
    estimatedMinutes: 5,
    startRoute: CARGO_ROUTE,
    icon: faMountain,
    group: 'module',
    steps: [
        {
            target: '[data-tour="tradeops-page"]',
            title: 'Welcome to Trade Ops Cargo',
            content:
                'This is your Cargo operations hub. Everything for solids trading - bids and offers, recaps, deliveries, budgets - lives on this single screen, organized by tabs.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            // ``.time-navigation-bar`` is position:absolute top-right. We
            // anchor on the element directly (not a wrapper) so floating-ui
            // gets real coordinates. Placement "left" keeps the tooltip
            // inside the viewport since the bar sits in the top-right
            // corner of the page.
            target: '.time-navigation-bar',
            title: 'Year navigation',
            content:
                'All tables on this screen are scoped to the year shown here. Step the year forward or back to see historical deals, recaps and deliveries. Refresh pulls the latest data without changing the year.',
            placement: 'left',
        },
        {
            target: '[data-tour="tradeops-tabs"]',
            title: 'Module tabs',
            content:
                'Switch between Shipment Overview (live map), Bid/Offer (deals in progress), Trade Recaps (signed contracts), Deliveries (execution) and - if you have access - Budget Calculator and Operations Access.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-deals-section"]',
            title: 'Bid/Offer tab',
            content:
                'Bids and offers are the deal cradle: every cargo trade starts here as a draft, gets negotiated, then matures into a recap. The table shows transaction number, counterparty, product and status.',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-deals"]',
        },
        {
            target: '[data-tour="tradeops-new-bid"]',
            title: 'Create a new bid',
            content:
                'Click New Bid (or New Offer) to open the Offer Generator. You enter product, counterparty, tonnage, price and terms; the system saves it as a draft and assigns a transaction number you can hand back to the desk.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-recaps-section"]',
            title: 'Trade Recaps tab',
            content:
                'Once a bid/offer is accepted it becomes a recap - the formal contract record. Sales recaps document what we sell, purchase recaps document what we buy, freight recaps capture shipping arrangements.',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-recaps"]',
        },
        {
            target: '[data-tour="tradeops-recap-actions"]',
            title: 'Create a recap',
            content:
                'Three buttons, three flows: Sales, Purchase or Freight. Each opens a stepper-driven form (Deal Details, Contract Partners, Commercial Terms, Deal Makers). Recaps drive everything downstream - deliveries, financials, invoicing.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-deliveries-section"]',
            title: 'Deliveries tab',
            content:
                'Deliveries are how recaps actually get executed: vessel, load and discharge ports, dates, lay times. One delivery can fulfil multiple recaps (paired sales/purchases on the same vessel).',
            placement: 'top',
            clickBefore: '[data-tour="tradeops-tab-deliveries"]',
        },
        {
            target: '[data-tour="tradeops-new-delivery"]',
            title: 'Create a delivery',
            content:
                'Click Create Delivery to open the delivery form. You link recaps, set vessel and ports, and the system tracks the shipment status across loading, in-transit, discharge and final settlement.',
            placement: 'bottom',
        },
        {
            target: '[data-tour="tradeops-page"]',
            title: 'You are good to go',
            content:
                'That is the Cargo tour. The same dashboard works for solids end-to-end: bid -> recap -> delivery, all year-scoped, all auditable. Re-run this tour any time from Hamster Training.',
            placement: 'center',
            disableBeacon: true,
            isLast: true,
        },
    ],
};

export default cargoTour;
